import { useAuthStore } from "@/lib/auth-store";
import { useDelegationStore } from "@/lib/delegation-store";
import { ApiRequestError } from "@/types/api";
import { useAuthPromptStore } from "@/hooks/use-require-auth";

// All requests go to same-origin /api/* — the Next.js proxy route
// forwards them to the backend and relays httpOnly cookies.

let refreshPromise: Promise<boolean> | null = null;

function inferLoginVerificationMetadata(
  path: string,
  status: number,
  message?: string,
): { requiresVerification?: boolean; credentialType?: string } {
  if (path !== "/api/auth/login" || status !== 401 || !message) {
    return {};
  }

  const normalizedMessage = message.toLowerCase();
  const looksLikePhoneVerification =
    normalizedMessage.includes("phone")
    && (
      normalizedMessage.includes("verification code sent")
      || normalizedMessage.includes("otp verification required")
      || normalizedMessage.includes("code sent to your phone")
    );
  const looksLikeEmailVerification = normalizedMessage.includes("verification sent to your email");

  if (looksLikePhoneVerification) {
    return { requiresVerification: true, credentialType: "phone" };
  }

  if (looksLikeEmailVerification) {
    return { requiresVerification: true, credentialType: "email" };
  }

  return {};
}

async function refreshTokens(): Promise<boolean> {
  // No body needed — the proxy extracts the refresh-token cookie
  // and injects it into the body for the backend.
  try {
    const res = await fetch("/api/token/refresh", {
      method: "POST",
      credentials: "include",
    });

    if (!res.ok) {
      useAuthStore.getState().logout();
      useAuthPromptStore.getState().open();
      return false;
    }

    // Refresh succeeded — new httpOnly cookies are set via Set-Cookie.
    // Update local auth state to stay in sync.
    useAuthStore.getState().setAuth();
    return true;
  } catch {
    useAuthStore.getState().logout();
    useAuthPromptStore.getState().open();
    return false;
  }
}

async function handleTokenRefresh(): Promise<boolean> {
  if (!refreshPromise) {
    refreshPromise = refreshTokens().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

async function requestResponse(
  path: string,
  options: RequestInit = {},
  { skipContentType = false }: { skipContentType?: boolean } = {},
): Promise<Response> {
  const headers: Record<string, string> = {
    ...(skipContentType ? {} : { "Content-Type": "application/json" }),
    ...((options.headers as Record<string, string>) ?? {}),
  };

  // No Bearer header — httpOnly cookie sent automatically (same-origin).

  const { onBehalfOfUserId } = useDelegationStore.getState();
  if (onBehalfOfUserId) {
    headers["X-On-Behalf-Of"] = onBehalfOfUserId;
  }

  let res: Response;
  try {
    res = await fetch(path, {
      credentials: "include",
      ...options,
      headers,
    });
  } catch {
    throw new ApiRequestError(0, "networkError");
  }

  // On 401, attempt token refresh — but NOT for public auth endpoints
  // where 401 means "bad credentials", not "session expired".
  const isAuthEndpoint = path.startsWith("/api/auth/");

  if (res.status === 401 && !isAuthEndpoint) {
    const refreshed = await handleTokenRefresh();
    if (refreshed) {
      try {
        res = await fetch(path, {
          credentials: "include",
          ...options,
          headers,
        });
      } catch {
        throw new ApiRequestError(0, "networkError");
      }
    } else {
      throw new ApiRequestError(401, "authRequired");
    }
  }

  if (res.status === 403 && onBehalfOfUserId) {
    useDelegationStore.getState().clearDelegation();
  }

  if (!res.ok) {
    let errorBody: {
      message?: string;
      reason?: string;
      errors?: Record<string, string[]>;
      requiresVerification?: boolean;
      credentialType?: string;
    } = {};
    try {
      errorBody = await res.json();
    } catch {
      // non-JSON error response
    }

    if (res.status === 401 && !isAuthEndpoint) {
      useAuthPromptStore.getState().open();
      throw new ApiRequestError(401, "authRequired");
    }

    const fallbackVerification = inferLoginVerificationMetadata(
      path,
      res.status,
      errorBody.message,
    );

    throw new ApiRequestError(
      res.status,
      errorBody.message ?? errorBody.reason ?? `Request failed with status ${res.status}`,
      errorBody.errors,
      errorBody.requiresVerification ?? fallbackVerification.requiresVerification,
      errorBody.credentialType ?? fallbackVerification.credentialType,
    );
  }

  return res;
}

async function request<T>(
  path: string,
  options: RequestInit = {},
  { skipContentType = false }: { skipContentType?: boolean } = {},
): Promise<T> {
  const res = await requestResponse(path, options, { skipContentType });

  if (res.status === 204) {
    return undefined as T;
  }

  return res.json();
}

export const apiClient = {
  get<T>(path: string, params?: Record<string, string | number>): Promise<T> {
    let url = path;
    if (params) {
      const searchParams = new URLSearchParams();
      for (const [key, value] of Object.entries(params)) {
        searchParams.set(key, String(value));
      }
      url = `${path}?${searchParams.toString()}`;
    }
    return request<T>(url);
  },

  post<T>(path: string, body?: unknown): Promise<T> {
    return request<T>(path, {
      method: "POST",
      body: body != null ? JSON.stringify(body) : undefined,
    });
  },

  put<T>(path: string, body?: unknown): Promise<T> {
    return request<T>(path, {
      method: "PUT",
      body: body != null ? JSON.stringify(body) : undefined,
    });
  },

  delete<T>(path: string): Promise<T> {
    return request<T>(path, { method: "DELETE" });
  },

  stream(
    path: string,
    options: RequestInit = {},
    { skipContentType = false }: { skipContentType?: boolean } = {},
  ): Promise<Response> {
    return requestResponse(path, options, { skipContentType });
  },

  upload<T>(path: string, file: File): Promise<T> {
    const formData = new FormData();
    formData.append("file", file);
    return request<T>(
      path,
      { method: "POST", body: formData },
      { skipContentType: true },
    );
  },

  postForm<T>(path: string, formData: FormData): Promise<T> {
    return request<T>(
      path,
      { method: "POST", body: formData },
      { skipContentType: true },
    );
  },
};
